import React from "react";
import {observer} from "mobx-react-lite";
import {Grid} from "@mui/material";
import {MithraEchart} from "../../../services/echarts/MithraEchart";
import {useSitStores} from "../../../stores";
import type {EChartContextType} from "../../../services/echarts/EChartProvider";
import type {EChartsType} from "echarts/types/dist/shared";
import {EchartSitDefaultColor, EchartSitDefaultLegend} from "../../../services/echarts/EchartSitCommonConfig";
import {SIT_TEST} from "../../SitApit";
import i18n from "../../../i18n/i18n";

const fin = (k: string) => i18n.t(`fin.${k}`, {ns: 'sit'});

// Compact currency-ish axis tick formatter (fixes "0,000" ticks — F1).
const fmtAxis = (v: number): string => {
    const a = Math.abs(v);
    if (a >= 1e9) return (v / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
    if (a >= 1e6) return (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (a >= 1e3) return (v / 1e3).toFixed(0) + 'K';
    return String(v);
};

function init(echart: EChartContextType, root: HTMLElement, data: any[]): EChartsType {
    const chart = echart.init(root, 'mithra', {
        renderer: 'svg',
        height: 280,
    });
    chart.setOption({
        tooltip: {
            trigger: 'axis',
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: {show: true},
            },
        },
        calculable: true,
        legend: {
            data: [fin('actual'), fin('target')],
            ...EchartSitDefaultLegend
        },
        // Global palette:
        color: EchartSitDefaultColor,
        // Reserve room so axis / annotation labels aren't clipped (F2).
        grid: {left: 8, right: 16, top: 48, bottom: 8, containLabel: true},
        xAxis: {type: 'time'},
        yAxis: {axisLabel: {formatter: fmtAxis}},
        dataset: data,
        series: [
            {
                name: fin('actual'), type: 'bar',
                encode: {x: 'month', y: 'value'},
                datasetId: 'Actual accumulative aggregated savingItems by month',
                color: '#141C3C', // --ac-navy (solid)
                emphasis: {focus: 'series'},
            },
            {
                name: fin('target'), type: 'line',
                encode: {x: 'month', y: 'target'},
                datasetId: 'Target accumulative aggregated savingItems by month',
                color: '#430944',
                // Target as a dashed line so it reads distinctly from Actual (F3).
                lineStyle: {type: 'dashed', width: 2},
                symbol: 'none',
                emphasis: {focus: 'series'},
            },
        ],
    })
    return chart;
}

export const TargetVsActualBar: React.FC = observer(() => {
    const {fbStore} = useSitStores();

    if (!fbStore.contributionPerDoi || !fbStore.cumulatedTargetData) {
        return <></>;
    }

    // Collapse the per-stage (DOI) contributions into a single "Actual" series
    // per month — the design shows Actual (solid) vs Target (dashed), not a
    // stage-gate stack (F3).
    const actualByMonth = new Map<number, number>();
    fbStore.contributionPerDoi.forEach(({data}) => data.forEach(row => {
        const key = new Date(row.month).getTime();
        actualByMonth.set(key, (actualByMonth.get(key) ?? 0) + row.value);
    }));
    const actualData = {
        id: 'Actual accumulative aggregated savingItems by month',
        dimensions: [
            {name: 'month', type: 'time'},
            {name: 'value', type: 'number'},
        ],
        source: [...actualByMonth.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([k, v]) => ([new Date(k), v])),
    };
    const targetLineData = {
        id: 'Target accumulative aggregated savingItems by month',
        dimensions: [
            {name: 'month', type: 'time'},
            {name: 'target', type: 'number'},
        ],
        source: fbStore.cumulatedTargetData.map(r => ([
            r.month,
            r.value,
        ])),
    };
    const data = [actualData, targetLineData]

    return (
        <Grid container>
            <Grid size={12}>
                {SIT_TEST && '<TargetVsActualBar/>'}
                <MithraEchart
                    // alwaysRerender={() => {
                    // }}
                    initChart={init}
                    data={data}
                    autoUpdateDataset
                    loading={fbStore.busy}
                />
            </Grid>
        </Grid>
    );
})